import React, { useState } from "react";
import { ArrowUpRight, CalendarDays, Utensils, Moon, X, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logoImg from "../assets/jalsa-logo.jpeg";

const LUNCH_SLOTS = [
  { time: "12:00 PM", available: true },
  { time: "12:30 PM", available: true },
  { time: "1:00 PM", available: false },
  { time: "1:30 PM", available: true },
  { time: "2:00 PM", available: false },
  { time: "2:30 PM", available: true },
  { time: "3:00 PM", available: true },
  { time: "3:30 PM", available: false },
  { time: "4:00 PM", available: true }
];

const DINNER_SLOTS = [
  { time: "4:00 PM", available: false },
  { time: "4:30 PM", available: true },
  { time: "5:00 PM", available: true },
  { time: "5:30 PM", available: true },
  { time: "6:00 PM", available: true },
  { time: "6:30 PM", available: true },
  { time: "7:00 PM", available: true },
  { time: "7:30 PM", available: true },
  { time: "8:00 PM", available: true },
  { time: "8:30 PM", available: true },
  { time: "9:00 PM", available: true },
  { time: "9:30 PM", available: true },
  { time: "10:00 PM", available: true },
  { time: "10:30 PM", available: true },
  { time: "11:00 PM", available: true },
  { time: "11:30 PM", available: true },
  { time: "12:00 AM", available: true },
  { time: "12:30 AM", available: true }
];

function ReservationModal({ reserveOpen, setReserveOpen }) {
  const [mealType, setMealType] = useState("dinner");
  const [selectedTime, setSelectedTime] = useState("4:30 PM");
  const [partySize, setPartySize] = useState("2 guests");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);

  const activeSlots = mealType === "lunch" ? LUNCH_SLOTS : DINNER_SLOTS;

  const handleMealChange = (type) => {
    setMealType(type);
    const newSlots = type === "lunch" ? LUNCH_SLOTS : DINNER_SLOTS;
    const firstAvailable = newSlots.find((s) => s.available);
    if (firstAvailable) {
      setSelectedTime(firstAvailable.time);
    }
  };

  return (
    <AnimatePresence>
      {reserveOpen && (
        <motion.div
          className="reservation-full-page"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.35, ease: "easeInOut" }}
        >
          <header className="reservation-header">
            <div className="res-header-left">
              <img src={logoImg} alt="Jalsa" className="res-logo" />
              <span>JALSA RESTOBAR</span>
            </div>
            <button
              className="back-to-site-btn"
              onClick={() => setReserveOpen(false)}
            >
              <ArrowLeft size={16} /> Back
            </button>
          </header>

          <main className="reservation-page-body">
            <div className="reservation-container">
              <div className="modal-kicker">
                <CalendarDays size={16} /> TABLE RESERVATION
              </div>
              <h2>
                Your table<br />
                <i>awaits.</i>
              </h2>
              <p className="res-subtitle">
                Select your session, date, and preferred time slot for a memorable dining experience.
              </p>

              <div className="meal-tabs">
                <button
                  type="button"
                  className={`meal-tab ${mealType === "lunch" ? "active" : ""}`}
                  onClick={() => handleMealChange("lunch")}
                >
                  <Utensils size={16} /> Lunch Session (12 PM - 4 PM)
                </button>
                <button
                  type="button"
                  className={`meal-tab ${mealType === "dinner" ? "active" : ""}`}
                  onClick={() => handleMealChange("dinner")}
                >
                  <Moon size={16} /> Dinner Session (4 PM - 12:30 AM / 1 AM)
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  alert(
                    `Reservation request submitted for ${partySize} on ${date} at ${selectedTime} (${mealType.toUpperCase()}).`
                  );
                  setReserveOpen(false);
                }}
              >
                <div className="form-row">
                  <label>
                    Party size
                    <select
                      value={partySize}
                      onChange={(e) => setPartySize(e.target.value)}
                    >
                      <option value="1 guest">1 guest</option>
                      <option value="2 guests">2 guests</option>
                      <option value="3 guests">3 guests</option>
                      <option value="4 guests">4 guests</option>
                      <option value="5 guests">5 guests</option>
                      <option value="6+ guests">6+ guests</option>
                    </select>
                  </label>
                  <label>
                    Date
                    <input
                      required
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </label>
                </div>

                <div className="form-row">
                  <label className="full-width-label">
                    Time
                    <select
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                    >
                      {activeSlots
                        .filter((s) => s.available)
                        .map((s) => (
                          <option key={s.time} value={s.time}>
                            {s.time}
                          </option>
                        ))}
                    </select>
                  </label>
                </div>

                <div className="time-slot-section">
                  <p className="time-slot-heading">
                    Choose an available time slot:
                  </p>
                  <div className="time-slots-grid">
                    {activeSlots.map((slot) => {
                      const isSelected = selectedTime === slot.time;
                      const isUnavailable = !slot.available;

                      return (
                        <button
                          key={slot.time}
                          type="button"
                          disabled={isUnavailable}
                          className={`time-slot-btn ${isSelected ? "selected" : ""
                            } ${isUnavailable ? "unavailable" : ""}`}
                          onClick={() => setSelectedTime(slot.time)}
                        >
                          <span className="time-text">{slot.time}</span>
                          {isUnavailable && <span className="slash-line" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="form-row margin-top-sm">
                  <label>
                    Full Name
                    <input required placeholder="Your full name" />
                  </label>
                  <label>
                    Phone Number
                    <input required type="tel" placeholder="+91 00000 00000" />
                  </label>
                </div>

                <button className="gold-btn modal-submit" type="submit">
                  Confirm Reservation <ArrowUpRight size={18} />
                </button>
              </form>
            </div>
          </main>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ReservationModal;
